import { useState, useEffect } from 'react';
import Navigation from '../../components/base/Navigation';
import Footer from '../../components/feature/Footer';

// Animation Hook
const useScrollAnimation = () => {
  const [visibleElements, setVisibleElements] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return visibleElements;
};

export default function About() {
  const visibleElements = useScrollAnimation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const heroImages = [
    'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&h=600&fit=crop',
    'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1920&h=600&fit=crop',
    'https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=1920&h=600&fit=crop',
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&h=600&fit=crop',
    'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1920&h=600&fit=crop',
    'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=1920&h=600&fit=crop'
  ];

  useEffect(() => {
    const getRandomInterval = () => Math.floor(Math.random() * 3000) + 4000;
    
    const changeImage = () => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
        setIsTransitioning(false);
      }, 500);
    };

    const intervalId = setInterval(changeImage, getRandomInterval());
    return () => clearInterval(intervalId);
  }, []);

  const handleDotClick = (index: number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentImageIndex(index);
      setIsTransitioning(false);
    }, 500);
  };

  const getAnimationClass = (elementId: string, baseClass: string = '') => {
    const isVisible = visibleElements.has(elementId);
    return `${baseClass} transition-all duration-1000 ease-out ${
      isVisible
        ? 'opacity-100 translate-y-0 scale-100'
        : 'opacity-0 translate-y-8 scale-95'
    }`;
  };

  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description: 'We use the latest AI technology to create websites that stand out and deliver real results for your business.'
    },
    {
      icon: 'ri-team-line',
      title: 'Partnership',
      description: 'Your success is our success. We build lasting relationships based on trust, communication, and results.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality',
      description: 'Every website we create undergoes thorough testing to ensure excellent performance and user experience.'
    },
    {
      icon: 'ri-time-line',
      title: 'Efficiency',
      description: 'We understand time matters. Most websites are completed within 5-7 business days without compromising quality.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Navigation />
      
      {/* Hero Section with Rotating Background */}
      <section className="pt-24 pb-16 relative overflow-hidden">
        {/* Background Images */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                index === currentImageIndex
                  ? 'opacity-100 scale-100 blur-0'
                  : 'opacity-0 scale-110 blur-sm'
              }`}
            >
              <img
                src={image}
                alt={`About background ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          
          {/* Gradient Overlays */}
          <div className={`absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60 transition-opacity duration-700 ${
            isTransitioning ? 'opacity-80' : 'opacity-100'
          }`}></div>
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/20 via-transparent to-teal-900/20"></div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div 
            id="about-hero" 
            data-animate 
            className={getAnimationClass('about-hero', 'text-center space-y-8')}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white font-space">
              About <span className="text-indigo-400">ClearPath Intelligence</span>
            </h1>
            <p className="text-xl text-slate-100 max-w-4xl mx-auto leading-relaxed">
              We're a Buffalo-based company dedicated to helping small businesses across the 
              United States achieve online success through intelligent web design and digital solutions.
            </p>
          </div>
        </div>

        {/* Image Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                index === currentImageIndex
                  ? 'w-8 h-3 bg-indigo-400'
                  : 'w-3 h-3 bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div 
              id="mission-content" 
              data-animate 
              className={getAnimationClass('mission-content', 'space-y-8')}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white font-space">
                Our <span className="text-indigo-400">Mission</span>
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                At ClearPath Intelligence, we believe every business deserves a website that works as hard as they do. 
                Our mission is to make professional web design accessible to small businesses everywhere by combining 
                advanced AI technology with human creativity and expertise.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed">
                We're not just building websites – we're creating digital foundations that help businesses grow, 
                connect with customers, and compete effectively in today's online marketplace. Every project we take on 
                is an opportunity to make a real difference in someone's business success.
              </p>
              <div className="flex items-center space-x-4 text-slate-400">
                <i className="ri-target-line text-indigo-400"></i>
                <span>Empowering businesses through intelligent web solutions</span>
              </div>
            </div>
            <div 
              id="mission-image" 
              data-animate 
              className={getAnimationClass('mission-image', '')}
              style={{ animationDelay: '0.2s' }}
            >
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop" 
                alt="Our Mission at Work"
                className="rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="values-header" 
            data-animate 
            className={getAnimationClass('values-header', 'text-center mb-16')}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-space">
              Our <span className="text-indigo-400">Values</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              These core principles guide everything we do and shape how we serve our clients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                id={`value-${index}`}
                data-animate 
                className={getAnimationClass(`value-${index}`, 'text-center space-y-6 p-8 rounded-xl bg-slate-700/30 border border-slate-600/20 hover:bg-slate-700/50 transition-all duration-300')}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 mx-auto bg-indigo-500/10 rounded-full flex items-center justify-center">
                  <i className={`${value.icon} text-3xl text-indigo-400`}></i>
                </div>
                <h3 className="text-xl font-semibold text-white">{value.title}</h3>
                <p className="text-slate-300 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div 
              id="story-image" 
              data-animate 
              className={getAnimationClass('story-image', '')}
            >
              <img 
                src="https://static.readdy.ai/image/9314f36c9b0b27794c5d4e89097301d2/1168eeca5d35c23596dfde3252d2d815.png" 
                alt="Buffalo NY Skyline"
                className="rounded-xl shadow-lg"
              />
            </div>
            <div 
              id="story-content" 
              data-animate 
              className={getAnimationClass('story-content', 'space-y-8')}
              style={{ animationDelay: '0.2s' }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white font-space">
                Why <span className="text-indigo-400">Buffalo</span>?
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                Buffalo, NY is experiencing a renaissance in technology and entrepreneurship. As a city built on 
                innovation and hard work, it's the perfect home for ClearPath Intelligence. We're proud to be 
                part of this community and help local businesses thrive in the digital age.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed">
                From our Buffalo headquarters, we serve small businesses across the entire United States, 
                bringing advanced technology capabilities to companies everywhere. Our roots in Western New York 
                keep us grounded and focused on the real needs of small business owners.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}